import React, { useState, useCallback, useRef, KeyboardEvent } from 'react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { Tag, X } from 'lucide-react';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
  showTagIcon?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags = [],
  onTagsChange,
  placeholder = 'Type a tag and press Enter',
  className = '',
  showTagIcon = true,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle adding a tag when Enter is pressed
  const handleAddTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag) {
        // Only add if it doesn't already exist
        if (!tags.includes(trimmedTag)) {
          onTagsChange([...tags, trimmedTag]);
        }
      }
      setInputValue('');
    },
    [tags, onTagsChange]
  );

  // Handle key down event for tag input (Enter key to add tags, allows spaces in tags)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue) {
        e.preventDefault();
        handleAddTag(inputValue);
      }
    },
    [inputValue, handleAddTag]
  );

  // Auto-save tag when input loses focus
  const handleBlur = useCallback(() => {
    if (inputValue) {
      handleAddTag(inputValue);
    }
  }, [inputValue, handleAddTag]);

  // Handle removing a tag
  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      onTagsChange(tags.filter((tag) => tag !== tagToRemove));
    },
    [tags, onTagsChange]
  );

  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 w-full cursor-text shadow-sm border rounded-md border-foreground/30 bg-background text-foreground placeholder:text-muted-foreground min-h-9 py-2 pl-3 pr-3 text-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-ring ${className}`}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className="flex items-center badge-amber gap-1 pr-2 !py-0"
        >
          {showTagIcon && <Tag className="w-3 h-3" />}
          {tag}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTag(tag);
            }}
            className="ml-1 hover:text-red-600"
            type="button"
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <input
        ref={inputRef}
        placeholder={tags.length ? '' : placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="flex-1 min-w-[120px] bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none border-none p-0"
      />
    </div>
  );
};
